import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Mail, RefreshCw, AlertCircle, CheckCircle2, MailX,
} from "lucide-react";
import { API } from "./constants";

// ── Email metrics card ──────────────────────────────────────────────────────
//
// Reads the rolling email-send tally stamped in `email_log` by every
// transactional helper. Lets the operator see at a glance:
//   * Is Brevo delivering (delivery_pct)?
//   * Which flows fired in the last 24h?
//   * Which sends failed and why (last 10 with reason)?

function StatBox({ label, value, tone, testid }) {
  const tones = {
    ok: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    neutral: "text-slate-200 border-slate-700 bg-slate-900/40",
    warn: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    bad: "text-red-300 border-red-500/30 bg-red-500/5",
  };
  return (
    <div
      data-testid={testid}
      className={`px-3 py-2.5 rounded-lg border ${tones[tone] || tones.neutral}`}
    >
      <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-xl font-bold mt-0.5">{value}</p>
    </div>
  );
}

export function EmailMetricsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(24);

  const load = useCallback(async (h) => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/superadmin/email-metrics?hours=${h}`,
        { withCredentials: true });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Error al cargar métricas");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(hours);
    // Auto-refresh every 2 min — emails arrive at human pace, no need to hammer.
    const interval = setInterval(() => load(hours), 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [hours, load]);

  const rows = data?.by_type || [];
  const totalSent = data?.total_sent ?? 0;
  const totalFailed = data?.total_failed ?? 0;
  const deliveryPct = data?.delivery_pct ?? 0;
  const total = data?.total ?? 0;
  const recentFailures = data?.recent_failures || [];

  const deliveryTone = totalFailed === 0
    ? "ok"
    : deliveryPct >= 90 ? "warn" : "bad";

  return (
    <div
      data-testid="email-metrics-card"
      className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
    >
      <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-violet-500/10 rounded-lg shrink-0">
            <Mail className="w-5 h-5 text-violet-300" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Emails transaccionales</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-[560px]">
              Resumen de cada flujo transaccional disparado. Si todas las cuentas
              muestran 0, probablemente falta configurar <code className="text-violet-300">BREVO_API_KEY</code> en Secrets.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="email-metrics-range">
          {[24, 24 * 7, 24 * 30].map((h) => (
            <button
              key={h}
              onClick={() => setHours(h)}
              data-testid={`email-metrics-range-${h}h`}
              className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                hours === h
                  ? "bg-violet-500/10 text-violet-300 border-violet-500/30"
                  : "text-slate-400 border-slate-700 hover:bg-slate-800"
              }`}
            >
              {h === 24 ? "24 h" : h === 168 ? "7 días" : "30 días"}
            </button>
          ))}
          <button
            onClick={() => load(hours)}
            disabled={refreshing}
            data-testid="email-metrics-refresh-btn"
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg disabled:opacity-50"
            title="Refrescar"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-500/10 border-b border-red-500/30 px-5 py-3">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="h-24 flex items-center justify-center text-slate-500 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Cargando métricas…
        </div>
      ) : (
        <div className="p-5 space-y-5">
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatBox label="Total intentos" value={total} tone="neutral" testid="email-stat-total" />
            <StatBox label="Entregados" value={totalSent} tone="ok" testid="email-stat-sent" />
            <StatBox label="Fallidos" value={totalFailed} tone={totalFailed > 0 ? "bad" : "neutral"} testid="email-stat-failed" />
            <StatBox label="Tasa entrega" value={`${deliveryPct}%`} tone={deliveryTone} testid="email-stat-delivery" />
          </div>

          {/* Per-type breakdown */}
          {total === 0 ? (
            <div
              data-testid="email-metrics-empty"
              className="text-center py-6 text-slate-500 text-sm border border-dashed border-slate-700 rounded-lg"
            >
              <MailX className="w-6 h-6 mx-auto mb-2 opacity-50" />
              Sin emails registrados en esta ventana.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="email-metrics-table">
                <thead>
                  <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-800">
                    <th className="py-2 pr-3 font-medium">Tipo</th>
                    <th className="py-2 pr-3 font-medium text-right">Entregados</th>
                    <th className="py-2 pr-3 font-medium text-right">Fallidos</th>
                    <th className="py-2 font-medium">Último envío</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {rows.map((r) => (
                    <tr
                      key={r.email_type}
                      data-testid={`email-row-${r.email_type}`}
                      className="hover:bg-slate-800/30"
                    >
                      <td className="py-2.5 pr-3 text-slate-200">
                        {r.label}
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        {r.sent > 0 ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400 font-mono">
                            <CheckCircle2 className="w-3 h-3" />
                            {r.sent}
                          </span>
                        ) : (
                          <span className="text-slate-600 font-mono">0</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        {r.failed > 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-400 font-mono">
                            <AlertCircle className="w-3 h-3" />
                            {r.failed}
                          </span>
                        ) : (
                          <span className="text-slate-600 font-mono">0</span>
                        )}
                      </td>
                      <td className="py-2.5 text-slate-400 text-xs font-mono">
                        {r.last_at
                          ? new Date(r.last_at).toLocaleString("es-MX", {
                              dateStyle: "short", timeStyle: "short",
                            })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent failures detail */}
          {recentFailures.length > 0 && (
            <div data-testid="email-recent-failures">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                Últimos fallos
              </p>
              <div className="space-y-1.5">
                {recentFailures.map((f, i) => (
                  <div
                    key={i}
                    data-testid={`email-failure-${i}`}
                    className="flex items-start gap-3 px-3 py-2 bg-red-500/5 border border-red-500/20 rounded-md text-xs"
                  >
                    <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-200 truncate">
                        <span className="text-slate-500">{f.email_type}</span>
                        {" → "}
                        <span className="font-mono">{f.to_email}</span>
                      </p>
                      <p className="text-red-300 mt-0.5 truncate" title={f.reason}>
                        {f.reason || "(sin mensaje)"}
                      </p>
                    </div>
                    <span className="text-slate-500 font-mono text-[10px] whitespace-nowrap">
                      {f.created_at
                        ? new Date(f.created_at).toLocaleString("es-MX", {
                            dateStyle: "short", timeStyle: "short",
                          })
                        : "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
